import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

// antd
import {
    Dropdown,
    Pagination,
    Row,
    Space,
    Spin,
    Table,
    Tag,
    theme,
    Typography,
} from 'antd'
import { EllipsisOutlined } from '@ant-design/icons'
import { Icon } from '@iconify/react'

// store
import { resetFilter } from '../store/candidate.slice'
import {
    changePage,
    getJobApplicantListByStatus,
    searchCandidate,
} from '../../jobApplications/store/jobApplicant.slice'
import { loginState } from '../../login/login.slice'
import { getTalentAccess } from '../../../helpers/reportsPermission'

// routes
import { PRIVATE_ROUTES } from '../../../routes'

// components
import ProfileAvatar from './profil-avatar'
import StatusModal from './status-modal'
import NoteDrawer from './candidate-note-drawer'
import { CANDIDATE_STATUS } from './candidate-status-tab-list'

// assets
import { NoteIcon, ViewIcon } from '../../../assets/icons'
import { StatusIcon } from '../../../assets/icons/statusIcon'

import dayjs from 'dayjs'
import styles from '../candidate.module.css'

const { Text } = Typography

const CandidateTable = () => {
    const { useToken } = theme
    const { token } = useToken()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { userProfile } = useSelector(loginState)
    const talentAccess = useMemo(
        () => getTalentAccess(userProfile),
        [userProfile]
    )

    const { activeTab, jobId } = useSelector((state) => state.candidateSlice)

    const {
        applicantList,
        applicantCount,
        applicantPageLimit,
        applicantCurrentPage,
        applicantIsNext,
        applicantSearch,
        loaders,
    } = useSelector((state) => state.jobApplicantSlice)

    const isLoading = Boolean(loaders?.applicantLoading)

    const [noteDrawer, setNoteDrawer] = useState(false)
    const [candidateNote, setCandidateNote] = useState(null)
    const [statusModal, setStatusModal] = useState({
        open: false,
        candidate: null,
        status: null,
    })
    const [sortInfo, setSortInfo] = useState({
        sort: null,
        sortType: null,
    })

    // Reset filters once on mount
    useEffect(() => {
        dispatch(resetFilter())
        dispatch(searchCandidate(''))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Fetch candidates whenever filters change
    useEffect(() => {
        dispatch(
            getJobApplicantListByStatus({
                filter: {
                    status: activeTab,
                    job: jobId,
                    search: applicantSearch,
                },
            })
        )
        dispatch(
            changePage({
                currentPage: 1,
                skip: 0,
                pageLimit: applicantPageLimit,
            })
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, sortInfo, applicantSearch, jobId])

    const handleTableChange = (_, __, sorter) => {
        const { columnKey, order } = sorter

        if (order === 'ascend' || order === 'descend') {
            const sortType = order === 'ascend' ? 'asc' : 'desc'
            setSortInfo({ sort: columnKey, sortType })
        } else {
            setSortInfo({ sort: null, sortType: null })
        }
    }

    const handlePaginationChange = async (page, limit) => {
        const skip = (page - 1) * limit
        await dispatch(
            getJobApplicantListByStatus({
                filter: { status: activeTab, job: jobId },
                skip,
                limit,
                sort: sortInfo.sort,
                sortType: sortInfo.sortType,
            })
        )
        await dispatch(
            changePage({
                currentPage: page,
                skip: (page - 1) * applicantPageLimit,
                pageLimit: limit,
            })
        )
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'fullName',
            key: 'fullName',
            sorter: true,
            sortOrder:
                sortInfo.sort === 'fullName'
                    ? sortInfo.sortType === 'asc'
                        ? 'ascend'
                        : 'descend'
                    : null,
            render: (text, item) => (
                <Space>
                    <ProfileAvatar
                        profilePicture={item?.profilePicture}
                        name={text}
                    />
                    <Text>{text || '-'}</Text>
                </Space>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'personalEmail',
            key: 'personalEmail',
            sorter: true,
            sortOrder:
                sortInfo.sort === 'personalEmail'
                    ? sortInfo.sortType === 'asc'
                        ? 'ascend'
                        : 'descend'
                    : null,
            render: (_, data) => {
                return <Text>{data?.candidate?.personalEmail || '-'}</Text>
            },
        },

        {
            title: 'Mobile Number',
            dataIndex: 'primaryContactNumber',
            key: 'primaryContactNumber',
            render: (_, data) => {
                const countryCode =
                    data?.candidate?.primaryContactNumberCountryCode || ''
                const phone = data?.candidate?.primaryContactNumber || ''

                if (!phone && !countryCode) {
                    return <Text>-</Text>
                }

                return (
                    <Text>
                        {countryCode ? (
                            <span className={styles.phoneCode}>
                                {countryCode}{' '}
                            </span>
                        ) : null}
                        <span className={styles.phoneNumber}>{phone}</span>
                    </Text>
                )
            },
        },
        {
            title: 'Experience',
            dataIndex: 'experience',
            key: 'experience',
            render: (_, data) => {
                return (
                    <Text>
                        {data?.candidate?.totalExperience
                            ? data?.candidate?.totalExperience
                            : '-'}
                    </Text>
                )
            },
        },
        {
            title: 'Status',
            dataIndex: 'applicationStatus',
            key: 'applicationStatus',
            render: (_, data) => {
                const status = CANDIDATE_STATUS.find(
                    (s) => s.value === data?.applicationStatus
                )

                if (!status) {
                    return <Text>-</Text>
                }

                return <Tag color={status.color}>{status.label}</Tag>
            },
        },
        {
            title: 'Job',
            dataIndex: 'jobs',
            key: 'jobs',
            render: (_, data) => {
                return (
                    <Text
                        style={{
                            fontWeight: 700,
                            color: token.colorPalette.baseColor.tertiary,
                            cursor: 'pointer',
                        }}
                        onClick={() =>
                            navigate(
                                PRIVATE_ROUTES.jobApplication(data?.jobs?._id)
                            )
                        }
                    >
                        {data?.jobs?.title}
                    </Text>
                )
            },
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (_, data) => {
                return (
                    <Text>
                        {data?.candidate?.createdAt
                            ? dayjs(
                                  new Date(data?.candidate?.createdAt)
                              ).format('DD/MM/YYYY')
                            : '-'}
                    </Text>
                )
            },
        },
        {
            key: 'context',
            dataIndex: 'context',
            title: ' ',
            render: (_, row) => {
                const menuItems = [
                    {
                        key: '1',
                        label: 'View',
                        icon: <ViewIcon />,
                        style: {
                            columnGap: '10px',
                        },
                        onClick: () => {
                            navigate(
                                PRIVATE_ROUTES.candidate.view(
                                    row?.candidateId
                                ),
                                {
                                    state: {
                                        jobId: row?.jobId,
                                        candidateId: row?.candidateId,
                                    },
                                }
                            )
                        },
                    },
                    {
                        key: '2',
                        label: 'Note',
                        icon: <NoteIcon width="20px" />,
                        style: {
                            columnGap: '10px',
                        },
                        onClick: () => {
                            setNoteDrawer(true)
                            setCandidateNote(row)
                        },
                    },
                ]

                if (talentAccess.canEdit && activeTab !== 'convertToUser') {
                    menuItems.push({
                        key: '3',
                        label: 'Change Status',
                        icon: <StatusIcon width="20px" />,
                        style: {
                            columnGap: '10px',
                        },
                        onClick: () => {
                            setStatusModal({
                                open: true,
                                candidate: row,
                                status: row?.applicationStatus,
                            })
                        },
                    })
                }

                return (
                    <Row justify="end" style={{ paddingRight: 5 }}>
                        <Dropdown
                            trigger={['click']}
                            placement="bottomRight"
                            menu={{
                                items: menuItems,
                            }}
                        >
                            <EllipsisOutlined rotate={90} />
                        </Dropdown>
                    </Row>
                )
            },
        },
    ]

    return (
        <div style={{ marginTop: '15px' }}>
            {/* Candidate Status Tabs */}
            {/* <Tabs
                defaultActiveKey="applied"
                activeKey={activeTab}
                onChange={(key) => dispatch(setActiveTab(key))}
                items={CANDIDATE_STATUS.map((status) => ({
                    key: status.value,
                    label: status.label,
                }))}
            /> */}

            <Spin spinning={isLoading}>
                <Table
                    columns={columns}
                    dataSource={isLoading ? [] : applicantList}
                    loading={false}
                    scroll={{ x: 'scroll' }}
                    pagination={false}
                    locale={{
                        emptyText: isLoading ? ' ' : undefined,
                    }}
                    rootClassName="tableGlobal"
                    style={{
                        borderRadius: '16px',
                        marginTop: '5px',
                        minHeight: isLoading ? 240 : undefined,
                    }}
                    showSorterTooltip={{ target: 'sorter-icon' }}
                    onChange={handleTableChange}
                />

                {applicantList?.length > 0 && (
                    <div className={styles.paginationBar}>
                        <Pagination
                            size="small"
                            current={applicantCurrentPage}
                            pageSize={applicantPageLimit}
                            pageSizeOptions={[5, 10, 15, 20, 50]}
                            showSizeChanger={{
                                className: styles.pageSizeSelect,
                                suffixIcon: (
                                    <Icon
                                        icon="mdi:chevron-down"
                                        width={12}
                                        height={12}
                                        color="#64748b"
                                    />
                                ),
                            }}
                            showTotal={(total, range) =>
                                `${range[0]} - ${range[1]} of ${total}`
                            }
                            itemRender={(page, type, originalElement) => {
                                if (type === 'prev') {
                                    return (
                                        <button
                                            type="button"
                                            className="ant-pagination-item-link"
                                            aria-label="Previous page"
                                        >
                                            <Icon
                                                icon="mdi:chevron-left"
                                                width={14}
                                                height={14}
                                            />
                                        </button>
                                    )
                                }
                                if (type === 'next') {
                                    return (
                                        <button
                                            type="button"
                                            className="ant-pagination-item-link"
                                            aria-label="Next page"
                                        >
                                            <Icon
                                                icon="mdi:chevron-right"
                                                width={14}
                                                height={14}
                                            />
                                        </button>
                                    )
                                }
                                return originalElement
                            }}
                            total={
                                applicantIsNext === false &&
                                applicantCurrentPage === 1
                                    ? applicantList?.length
                                    : applicantCount
                            }
                            onChange={handlePaginationChange}
                        />
                    </div>
                )}
            </Spin>

            {/* Drawers & Modals */}
            <NoteDrawer
                visible={noteDrawer}
                onClose={setNoteDrawer}
                candidate={candidateNote}
            />
            <StatusModal
                activeStatus={activeTab}
                statusModal={statusModal}
                setStatusModal={setStatusModal}
                open={statusModal.open}
                selectedStatus={statusModal?.status}
                isDetails={
                    statusModal?.candidate?.candidate
                        ?.isCompleteCandidateDetails
                }
                onClose={() => setStatusModal({ open: false, candidate: null })}
                onChange={(newStatus) =>
                    setStatusModal((prev) => ({
                        ...prev,
                        status: newStatus,
                    }))
                }
            />
        </div>
    )
}

export default CandidateTable
